import { makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { ottoTokens } from '@/theme/ottoTokens';

/**
 * Shared styles for the header-navigation pill family.
 *
 * All three pills share the same neutral background/border treatment
 * (rounded, neutral surface with subtle stroke). Differences are semantic
 * rather than visual:
 *  - WorkspacePill     — region + project, rare/occasional switch
 *  - AppPill           — app, occasional switch
 *  - WorkflowLeafPill  — workflow, frequent switch · focal element
 *                        (uses a slightly stronger stroke and carries the
 *                        status-indicator dot)
 */
export const usePillStyles = makeStyles({
  // ─── WorkspacePill (project) ──────────────────────────────────────
  workspacePill: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    paddingInline: tokens.spacingHorizontalM,
    paddingBlock: tokens.spacingVerticalXS,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusCircular,
    cursor: 'pointer',
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    minWidth: 0,
    transitionProperty: 'background-color, border-color, transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    // Extend the click target vertically to ~40px without affecting visual
    // size or overlapping horizontal neighbours (chevron separator + next pill).
    '::before': {
      content: '""',
      position: 'absolute',
      insetBlock: '-7px',
      insetInline: '0',
    },
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      ...shorthands.borderColor(tokens.colorNeutralStroke1),
    },
    ':active': {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
      transform: 'scale(0.96)',
    },
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: '-2px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    '@media (forced-colors: active)': {
      ...shorthands.borderColor('Highlight'),
    },
  },

  // ─── AppPill (neutral) ────────────────────────────────────────────
  appPill: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    paddingInline: tokens.spacingHorizontalM,
    paddingBlock: tokens.spacingVerticalXS,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusCircular,
    cursor: 'pointer',
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    minWidth: 0,
    transitionProperty: 'background-color, border-color, transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    '::before': {
      content: '""',
      position: 'absolute',
      insetBlock: '-7px',
      insetInline: '0',
    },
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      ...shorthands.borderColor(tokens.colorNeutralStroke1),
    },
    ':active': {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
      transform: 'scale(0.96)',
    },
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: '-2px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },

  // ─── WorkflowLeafPill (focal, with status indicator) ──────────────
  leafPill: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    paddingInline: tokens.spacingHorizontalM,
    paddingBlock: tokens.spacingVerticalXS,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    borderRadius: tokens.borderRadiusCircular,
    cursor: 'pointer',
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    minWidth: 0,
    transitionProperty: 'background-color, border-color, transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    '::before': {
      content: '""',
      position: 'absolute',
      insetBlock: '-7px',
      insetInline: '0',
    },
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':active': {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
      transform: 'scale(0.96)',
    },
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: '-2px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },

  // ─── Shared bits ──────────────────────────────────────────────────
  pillIcon: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  pillLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    maxWidth: '180px',
  },
  pillPrefix: {
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
    flexShrink: 0,
  },
  pillChevron: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },

  // Status indicator dot (used inside leaf pill)
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: tokens.borderRadiusCircular,
    flexShrink: 0,
    '@media (forced-colors: active)': {
      forcedColorAdjust: 'none',
      backgroundColor: 'Highlight',
    },
  },
  iconPlaceholder: {
    display: 'inline-block',
    width: '16px',
    height: '16px',
  },
  menuItemRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    width: '100%',
    minWidth: 0,
    // Hovering either the resource name or the "Go to …" hint underlines
    // both, so they read as a single hyperlink affordance. Individual
    // :hover rules on the children would only underline whichever span the
    // cursor is over, which feels fragmented.
    ':hover .otto-pill-link': {
      textDecorationLine: 'underline',
    },
  },
  menuItemLabel: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    flex: '1 1 auto',
  },
  // Label variant used on the current row whenever the row should read as a
  // hyperlink — either alongside a "Go to …" action hint (Workspace/App
  // pills) or via the `highlightCurrentAsLink` opt-in (WorkflowLeafPill).
  // Brand color at rest; underline is applied via menuItemRow's :hover so
  // both label and (optional) hint underline together.
  menuItemLabelLink: {
    color: tokens.colorBrandForegroundLink,
  },
  inlineLink: {
    color: tokens.colorBrandForegroundLink,
    fontSize: tokens.fontSizeBase200,
    flexShrink: 0,
  },
  statusRunning:   { backgroundColor: ottoTokens.statusRunning },
  statusSucceeded: { backgroundColor: ottoTokens.statusSucceeded },
  statusFailed:    { backgroundColor: ottoTokens.statusFailed },
  statusDraft:     { backgroundColor: ottoTokens.statusWaiting },
  statusIdle:      { backgroundColor: tokens.colorNeutralForeground4 },

  // Menu popover sizing & search input
  menuPopover: {
    minWidth: '320px',
    maxWidth: '440px',
    paddingInline: tokens.spacingHorizontalXXS,
    paddingBlock: tokens.spacingVerticalXXS,
    // Inset every menu item slightly from the popover walls and round
    // its corners. Hover/focus backgrounds were previously flush with
    // the popover edge, which read as a flat bar; rounded inset cards
    // match the convention used in modern command menus.
    //
    // Menuitem outer-edge math (must align with the search input below).
    //
    // Match the searchContainer's marginInline exactly so any inset added
    // by Fluent's MenuList (padding, focus-ring gutter, scrollbar
    // reservation, etc.) lands symmetrically on both edges. Worst case the
    // menuitem ends up uniformly narrower than the search input; best case
    // both align perfectly. Either way we avoid the asymmetric mismatch
    // that prior attempts produced.
    //
    //   search outer.inset   = popover paddingInline (XXS=2) + searchContainer marginInline (M=12) = 14px (both sides)
    //   menuitem outer.inset = popover paddingInline (XXS=2) + MenuList padding + marginInline (M=12)
    '& [role="menuitem"], & [role="menuitemradio"]': {
      marginInline: tokens.spacingHorizontalM,
      borderRadius: tokens.borderRadiusMedium,
      // Force the menuitem itself to honour the popover width and let its
      // content children shrink so long labels can ellipsize instead of
      // overflowing past the popover edge.
      overflow: 'hidden',
      '& > *': {
        minWidth: 0,
      },
    },
  },
  searchContainer: {
    marginInline: tokens.spacingHorizontalM,
    paddingBlock: tokens.spacingVerticalXS,
  },
  searchInput: {
    width: '100%',
  },
  currentItem: {
    backgroundColor: tokens.colorBrandBackground2,
    fontWeight: tokens.fontWeightSemibold,
    borderRadius: tokens.borderRadiusMedium,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2Hover,
    },
  },
  scrollableList: {
    // Approximately 12 rows × ~36px (Fluent MenuItem ~32px + 4px inset
    // padding) before the scrollbar kicks in. `min(...)` keeps the menu
    // from exceeding ~60% of the viewport on short screens.
    maxHeight: 'min(60vh, 28rem)',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingBlock: tokens.spacingVerticalXXS,
  },
  noResults: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontStyle: 'italic',
    paddingInline: tokens.spacingHorizontalM,
    paddingBlock: tokens.spacingVerticalS,
  },
  checkmark: {
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  menuItemSection: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    paddingInline: tokens.spacingHorizontalM,
    paddingBlock: `${tokens.spacingVerticalXS} ${tokens.spacingVerticalXXS}`,
  },
});
